import { FC, useMemo, useState, useEffect } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { deepEqual } from 'fast-equals';
import { Add, Save, Close, Work } from '@mui/icons-material';
import { useQuery } from 'react-query';
// Components
import { Modal, Loader, SMBSelect } from '../../../../components';
import {
  Fade,
  ButtonGroup,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  Autocomplete,
  Box,
  Divider,
  Card as CardMui,
  CardContent,
  CardHeader,
  Paper
} from '@mui/material';
// models
// fetch
import { useSnackbar } from 'notistack';
import {
  IDropdownResponse,
  IStandardFeatureDetail,
  IStandardFeaturePayload,
  IUpgradeOptions
} from '../../../../models';
import {
  createStandardFeature,
  getOptionLockouts,
  getOptionPhases,
  getOptionTerritories,
  getStandardFeaturelookup,
  getStandardFeatureOptionSets,
  updateStandardFeatureDetail,
  updateStandardFeatureImage,
  getStandardFeatureBuildOptions
} from '../../../../fetch';
import { StandardFeatureImages } from './standard-feature-images';

interface IStandardFeatureModal {
  standardFeatureId: string;
  standardFeatureDetail: IStandardFeatureDetail | null;
  refetchstandardFeatureDetail: () => void;
  refetchstandardFeatures: () => void;
  open: boolean;
  modalIsLoading: boolean;
  onClose: () => void;
}

const StandardFeatureSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  phaseId: Yup.string().required('Required'),
  optionSetId: Yup.string().required('Required'),
  alwaysIncluded: Yup.boolean(),
  normalRetailRate: Yup.string(),
  retailRateIsSF: Yup.string(),
  imageUrl: Yup.string(),
  notes: Yup.string().max(100, 'Max 100 characters'),
  excludedTerritories: Yup.array().of(Yup.number()),
  lockedOptions: Yup.array().of(Yup.number()),
  lockedStandardFeatures: Yup.array().of(Yup.number())
});

export const StandardFeatureModal: FC<IStandardFeatureModal> = ({
  standardFeatureId,
  standardFeatureDetail,
  refetchstandardFeatureDetail,
  refetchstandardFeatures,
  open,
  modalIsLoading,
  onClose
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const isEdit = standardFeatureId !== 'add';
  //State

  const [selectedLockedOptionValues, setSelectedLockedOptionValues] = useState<IDropdownResponse[]>(
    []
  );
  const [selectedLockedStandardFeaturesValues, setSelectedLockedStandardFeaturesValues] = useState<
    IDropdownResponse[]
  >([]);
  const [inputSelectedLockedOption, setInputSelectedLockedOption] = useState('');
  const [inputSelectedLockedStandardFeature, setInputSelectedLockedStandardFeature] = useState('');

  const [inputSelectedTerritory, setInputSelectedTerritory] = useState('');
  const [selectedTerritoryValues, setSelectedTerritoryValues] = useState<IDropdownResponse[]>([]);
  const [imageFormData, setImageFormData] = useState<any>();
  const [selectedCategory, setCategory] = useState('');
  const [selectedSubcategoryValues, setSelectedSubcategoryValues] = useState<any>([]);

  const handleClose = () => {
    onClose();
    setSelectedLockedOptionValues([]);
    setSelectedLockedStandardFeaturesValues([]);
    setSelectedTerritoryValues([]);
  };

  //API calls
  const { isLoading: isLoadingOptionPhases, data: optionPhases } = useQuery<
    IDropdownResponse[],
    Error
  >(['getOptionPhases'], () => getOptionPhases());
  const { isLoading: isLoadingOptionSets, data: optionSets } = useQuery<IDropdownResponse[], Error>(
    ['getOptionSets'],
    () => getStandardFeatureOptionSets()
  );
  const { isLoading: isLoadingLockedOptions, data: lockedOptions } = useQuery<
    IDropdownResponse[],
    Error
  >(['getLockouts', open], () => getOptionLockouts(), {
    onSuccess: d => {
      const options = d.filter(record => {
        return standardFeatureDetail?.lockedOptions?.includes(Number(record.value));
      });
      setSelectedLockedOptionValues(options);
    },
    enabled: standardFeatureDetail !== undefined || standardFeatureId === 'add'
  });

  const lockedOptionData = useMemo(() => lockedOptions ?? [], [lockedOptions]);

  const { isLoading: isLoadingOptionTerritories, data: optionTerritories } = useQuery<
    IDropdownResponse[],
    Error
  >(['getOptionTerritories', open], () => getOptionTerritories(), {
    onSuccess: d => {
      const options = d.filter(record => {
        return standardFeatureDetail?.excludedTerritories?.includes(Number(record.value));
      });
      setSelectedTerritoryValues(options);
    },
    enabled: standardFeatureDetail !== undefined || standardFeatureId === 'add'
  });
  const optionTerritoryData = useMemo(() => optionTerritories ?? [], [optionTerritories]);

  const { isLoading: isLoadingLockedStandardFeatures, data: lockedStandardFeatures } = useQuery<
    IDropdownResponse[],
    Error
  >(['getStandardFeatures', open], () => getStandardFeaturelookup(), {
    onSuccess: d => {
      const options = d.filter(record => {
        return standardFeatureDetail?.lockedStandardFeatures?.includes(Number(record.value));
      });
      setSelectedLockedStandardFeaturesValues(options);
    },
    enabled: standardFeatureDetail !== undefined || standardFeatureId === 'add'
  });
  const lockedStandardFeatureData = useMemo(
    () => lockedStandardFeatures ?? [],
    [lockedStandardFeatures]
  );

  const { isLoading: isLoadingStandardFeatureBuildOptions, data: buildOptions } = useQuery<
    any[],
    Error
  >(['getStandardFeatureBuildOptions'], () => getStandardFeatureBuildOptions());
  const categoryOptions = buildOptions?.map(option => option?.category);

  const selectedCategorySubcategories = useMemo(() => {
    const categoryObject = buildOptions?.find(option => option.category === selectedCategory);
    return categoryObject ? categoryObject.subcategories : [];
  }, [selectedCategory, buildOptions]);

  const selectedOptions = useMemo(() => {
    const allOptions = selectedSubcategoryValues?.reduce((acc, subcategory) => {
      const subcategoryOptions = subcategory?.options || [];
      return acc.concat(subcategoryOptions);
    }, []);
    return allOptions;
  }, [selectedSubcategoryValues]);

  useEffect(() => {
    if (buildOptions && standardFeatureDetail?.upgradeOptions?.length > 0) {
      // Create a map or set that holds both optionName and optionSetId for comparison
      const optionIdentifiers = new Set(
        standardFeatureDetail.upgradeOptions.map(
          option => `${option.optionName}-${option.optionSetId}`
        )
      );

      let foundSubcategories = [];
      let foundCategory = '';

      // Traverse the buildOptions to find matching options and their parent subcategories and category
      for (const category of buildOptions) {
        for (const subcategory of category.subcategories) {
          const matchingOptions = subcategory.options.filter(option =>
            optionIdentifiers.has(`${option.optionName}-${option.optionSetId}`)
          );

          if (matchingOptions.length > 0) {
            // If there are matching options in this subcategory, add the subcategory to the found list
            foundSubcategories.push(subcategory);

            // If we haven't set the foundCategory yet, set it to the current category
            if (!foundCategory) {
              foundCategory = category.category;
            }
          }
        }
      }

      setSelectedSubcategoryValues(foundSubcategories);
      setCategory(foundCategory);
    }
  }, [buildOptions, standardFeatureDetail?.upgradeOptions]);

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          name: standardFeatureDetail?.name ?? '',
          phaseId: standardFeatureDetail?.phaseId ?? optionPhases?.[0]?.value ?? '',
          optionSetId: standardFeatureDetail?.optionSetId ?? optionSets?.[0]?.value ?? '',
          alwaysInclude: standardFeatureDetail?.alwaysInclude === true ? 'Yes' : 'No',
          includeInCalculator: standardFeatureDetail?.includeInCalculator === true ? 'Yes' : 'No',
          normalRetailRate: standardFeatureDetail?.normalRetailRate ?? '',
          retailRateIsSF: standardFeatureDetail?.retailRateIsSF === true ? 'Yes' : 'No',
          imageUrl: standardFeatureDetail?.imageUrl ?? '',
          notes: standardFeatureDetail?.notes ?? '',
          excludedTerritories: standardFeatureDetail?.excludedTerritories ?? [],
          lockedOptions: standardFeatureDetail?.lockedOptions ?? [],
          lockedStandardFeatures: standardFeatureDetail?.lockedStandardFeatures ?? [],
          alwaysDisplayInOptionsReport:
            standardFeatureDetail?.alwaysDisplayInOptionsReport === true ? 'Yes' : 'No',
          upgradeOptions: standardFeatureDetail?.upgradeOptions ?? []
        }}
        validationSchema={StandardFeatureSchema}
        onSubmit={async (values, actions) => {
          const data: IStandardFeaturePayload = {
            ...values,
            phaseId: Number(values.phaseId),
            optionSetId: Number(values.optionSetId),
            alwaysInclude: values.alwaysInclude === 'Yes' ? true : false,
            alwaysDisplayInOptionsReport:
              values.alwaysDisplayInOptionsReport === 'Yes' ? true : false,
            includeInCalculator: values.includeInCalculator === 'Yes' ? true : false,
            retailRateIsSF:
              values.includeInCalculator === 'Yes'
                ? values.retailRateIsSF === 'Yes'
                  ? true
                  : false
                : null,
            excludedTerritories: values.excludedTerritories.map((item: any) => Number(item)),
            lockedOptions: values.lockedOptions.map((item: any) => Number(item)),
            lockedStandardFeatures: values.lockedStandardFeatures.map((item: any) => Number(item)),
            normalRetailRate:
              values.includeInCalculator === 'Yes' ? Number(values.normalRetailRate) : null
          };
          if (standardFeatureId !== 'add') {
            try {
              await updateStandardFeatureDetail(standardFeatureId, data);
              refetchstandardFeatures();
              enqueueSnackbar('Standard Feature updated!', {
                variant: 'success'
              });
              handleClose();
              actions.resetForm();
              setCategory('');
              setSelectedSubcategoryValues([]);
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'Error updating Standard Feature.', {
                variant: 'error'
              });
            }
          }
          if (standardFeatureId === 'add') {
            try {
              const standardFeature = await createStandardFeature(data);
              if (imageFormData) {
                await updateStandardFeatureImage(standardFeature, imageFormData);
              }
              refetchstandardFeatures();

              enqueueSnackbar('Standard Feature added!', {
                variant: 'success'
              });
              handleClose();
              actions.resetForm();
              setCategory('');
              setSelectedSubcategoryValues([]);
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'Error creating Standard Feature.', {
                variant: 'error'
              });
            }
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          initialValues,
          setFieldValue,
          errors,
          touched,
          handleSubmit,
          dirty,
          isValid,
          handleBlur
        }) => {
          return (
            <Modal
              open={open}
              onClose={() => {
                if (!deepEqual(initialValues, values)) {
                  const result = window.confirm(
                    'You have unsaved changes, are you sure you want to exit?'
                  );
                  if (result) {
                    resetForm();
                    handleClose();
                    setCategory('');
                    setSelectedSubcategoryValues([]);
                  } else {
                    return;
                  }
                } else {
                  handleClose();
                  resetForm();
                  setCategory('');
                  setSelectedSubcategoryValues([]);
                }
              }}
              maxWidth="lg"
              title={isEdit ? 'Edit Standard Feature' : 'Add Standard Feature'}
            >
              {modalIsLoading && <Loader type="overlay" position="centered" />}
              <Fade in={open}>
                <Form onSubmit={handleSubmit} autoComplete="none">
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <CardMui elevation={0} variant="outlined" className={classes.marginBottom}>
                        <CardHeader
                          avatar={<Work />}
                          title="INFO"
                          className={classes.primaryHeader}
                        />
                        <CardContent className={classes.cardContent}>
                          <Paper elevation={0} square>
                            <div className={classes.content}>
                              <Grid container spacing={1}>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    required
                                    fullWidth
                                    variant="outlined"
                                    autoComplete="nope"
                                    label="Name"
                                    name="name"
                                    size="small"
                                    value={values.name}
                                    onBlur={handleBlur}
                                    onChange={e => setFieldValue('name', e.target.value)}
                                    error={touched?.name && errors?.name ? true : false}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <SMBSelect
                                    required
                                    onChange={e => {
                                      setFieldValue('phaseId', e.target.value);
                                    }}
                                    variant="outlined"
                                    name={`phaseId`}
                                    label={`Phase`}
                                    id={`phaseId`}
                                    value={values.phaseId}
                                    onBlur={handleBlur}
                                    disabled={isLoadingOptionPhases}
                                    error={touched?.phaseId && errors?.phaseId ? true : false}
                                  >
                                    {optionPhases?.map((phase: IDropdownResponse, index) => {
                                      return (
                                        <MenuItem key={`${index + 1}`} value={phase.value}>
                                          {phase.description}
                                        </MenuItem>
                                      );
                                    })}
                                  </SMBSelect>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <SMBSelect
                                    required
                                    onChange={e => {
                                      setFieldValue('optionSetId', e.target.value);
                                    }}
                                    variant="outlined"
                                    name={`optionSetId`}
                                    label={`Option Set`}
                                    id={`optionSetId`}
                                    value={values.optionSetId}
                                    onBlur={handleBlur}
                                    disabled={isLoadingOptionSets}
                                    error={
                                      touched?.optionSetId && errors?.optionSetId ? true : false
                                    }
                                  >
                                    {optionSets?.map((set: IDropdownResponse, index) => {
                                      return (
                                        <MenuItem key={`${index + 1}`} value={set.value}>
                                          {set.description}
                                        </MenuItem>
                                      );
                                    })}
                                  </SMBSelect>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <SMBSelect
                                    required
                                    onChange={e => {
                                      setFieldValue('alwaysInclude', e.target.value);
                                    }}
                                    variant="outlined"
                                    name={`alwaysInclude`}
                                    label={`Always Included`}
                                    id={`alwaysInclude`}
                                    value={values.alwaysInclude}
                                    onBlur={handleBlur}
                                    error={
                                      touched?.alwaysInclude && errors?.alwaysInclude ? true : false
                                    }
                                  >
                                    {[
                                      { description: 'Yes', value: 'Yes' },
                                      { description: 'No', value: 'No' }
                                    ]?.map((set: IDropdownResponse, index) => {
                                      return (
                                        <MenuItem key={`${index + 1}`} value={set.value}>
                                          {set.description}
                                        </MenuItem>
                                      );
                                    })}
                                  </SMBSelect>
                                </Grid>

                                <Grid item container xs={12} spacing={1}>
                                  <Grid item xs={12} sm={6}>
                                    <SMBSelect
                                      required
                                      onChange={e => {
                                        setFieldValue('includeInCalculator', e.target.value);
                                      }}
                                      variant="outlined"
                                      name={`includeInCalculator`}
                                      label={`Include in Calculator`}
                                      id={`includeInCalculator`}
                                      value={values.includeInCalculator}
                                      onBlur={handleBlur}
                                      error={
                                        touched?.includeInCalculator && errors?.includeInCalculator
                                          ? true
                                          : false
                                      }
                                    >
                                      {[
                                        { description: 'Yes', value: 'Yes' },
                                        { description: 'No', value: 'No' }
                                      ]?.map((set: IDropdownResponse, index) => {
                                        return (
                                          <MenuItem key={`${index + 1}`} value={set.value}>
                                            {set.description}
                                          </MenuItem>
                                        );
                                      })}
                                    </SMBSelect>
                                  </Grid>

                                  {values?.includeInCalculator === 'Yes' && (
                                    <Grid item xs={12} sm={6}>
                                      <TextField
                                        required={values?.includeInCalculator === 'Yes'}
                                        fullWidth
                                        type="number"
                                        variant="outlined"
                                        autoComplete="nope"
                                        label="Normal Retail Rate"
                                        name="normalRetailRate"
                                        size="small"
                                        value={values.normalRetailRate}
                                        onBlur={handleBlur}
                                        onChange={e =>
                                          setFieldValue('normalRetailRate', e.target.value)
                                        }
                                        error={
                                          touched?.normalRetailRate && errors?.normalRetailRate
                                            ? true
                                            : false
                                        }
                                      />
                                      <RadioGroup
                                        row
                                        style={{ justifyContent: 'space-evenly' }}
                                        name="use-radio-group"
                                        value={values?.retailRateIsSF}
                                        onChange={e =>
                                          setFieldValue('retailRateIsSF', e.target.value)
                                        }
                                      >
                                        <div>
                                          <FormControlLabel
                                            value="Yes"
                                            label="SF"
                                            control={<Radio />}
                                          />
                                          <FormControlLabel
                                            value="No"
                                            label="LS"
                                            control={<Radio />}
                                          />
                                        </div>
                                      </RadioGroup>
                                    </Grid>
                                  )}
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    multiline
                                    rows={1}
                                    variant="outlined"
                                    autoComplete="nope"
                                    label="Notes"
                                    name="notes"
                                    size="small"
                                    value={values.notes}
                                    onBlur={handleBlur}
                                    onChange={e => setFieldValue('notes', e.target.value)}
                                    error={touched?.notes && errors?.notes ? true : false}
                                    helperText={touched?.notes && errors?.notes}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Autocomplete
                                    multiple
                                    id="excludedTerritories"
                                    value={selectedTerritoryValues}
                                    onChange={(event, newValue: any[]) => {
                                      setSelectedTerritoryValues(newValue);
                                      setFieldValue(
                                        'excludedTerritories',
                                        newValue.map(val => val.value)
                                      );
                                    }}
                                    inputValue={inputSelectedTerritory}
                                    onInputChange={(event, newInputValue) => {
                                      setInputSelectedTerritory(newInputValue);
                                    }}
                                    options={optionTerritoryData}
                                    getOptionLabel={option => option.description}
                                    filterSelectedOptions
                                    renderOption={(props, option: any) => {
                                      return (
                                        <li {...props} key={`${option.value}`}>
                                          {option.description}
                                        </li>
                                      );
                                    }}
                                    renderInput={params => (
                                      <TextField
                                        {...params}
                                        disabled={isLoadingOptionTerritories}
                                        label={
                                          isLoadingOptionTerritories
                                            ? 'Loading...'
                                            : 'Excluded Territories'
                                        }
                                        placeholder="Territories"
                                      />
                                    )}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Autocomplete
                                    multiple
                                    id="lockedOptions"
                                    value={selectedLockedOptionValues}
                                    onChange={(event, newValue: any[]) => {
                                      setSelectedLockedOptionValues(newValue);
                                      setFieldValue(
                                        'lockedOptions',
                                        newValue.map(val => val.value)
                                      );
                                    }}
                                    inputValue={inputSelectedLockedOption}
                                    onInputChange={(event, newInputValue) => {
                                      setInputSelectedLockedOption(newInputValue);
                                    }}
                                    options={lockedOptionData}
                                    getOptionLabel={option => option.description}
                                    filterSelectedOptions
                                    renderOption={(props, option: any) => {
                                      return (
                                        <li {...props} key={`${option.value}`}>
                                          {option.description}
                                        </li>
                                      );
                                    }}
                                    renderInput={params => (
                                      <TextField
                                        {...params}
                                        disabled={isLoadingLockedOptions}
                                        label={
                                          isLoadingLockedOptions ? 'Loading...' : 'Option Lockouts'
                                        }
                                        placeholder="Options"
                                      />
                                    )}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Autocomplete
                                    multiple
                                    id="lockedStandardFeatures"
                                    value={selectedLockedStandardFeaturesValues}
                                    onChange={(event, newValue: any[]) => {
                                      setSelectedLockedStandardFeaturesValues(newValue);
                                      setFieldValue(
                                        'lockedStandardFeatures',
                                        newValue.map(val => val.value)
                                      );
                                    }}
                                    inputValue={inputSelectedLockedStandardFeature}
                                    onInputChange={(event, newInputValue) => {
                                      setInputSelectedLockedStandardFeature(newInputValue);
                                    }}
                                    options={lockedStandardFeatureData}
                                    getOptionLabel={option => option.description}
                                    filterSelectedOptions
                                    renderOption={(props, option: any) => {
                                      return (
                                        <li {...props} key={`${option.value}`}>
                                          {option.description}
                                        </li>
                                      );
                                    }}
                                    renderInput={params => (
                                      <TextField
                                        {...params}
                                        disabled={isLoadingLockedStandardFeatures}
                                        label={
                                          isLoadingLockedStandardFeatures
                                            ? 'Loading...'
                                            : 'Standard Feature Lockouts'
                                        }
                                        InputLabelProps={{ shrink: true }}
                                        placeholder="Features"
                                      />
                                    )}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <SMBSelect
                                    required
                                    onChange={e => {
                                      setFieldValue('alwaysDisplayInOptionsReport', e.target.value);
                                    }}
                                    variant="outlined"
                                    name={`alwaysDisplayInOptionsReport`}
                                    label={`Always Display in Options Report`}
                                    id={`alwaysDisplayInOptionsReport`}
                                    value={values.alwaysDisplayInOptionsReport}
                                    onBlur={handleBlur}
                                    error={
                                      touched?.alwaysDisplayInOptionsReport &&
                                      errors?.alwaysDisplayInOptionsReport
                                        ? true
                                        : false
                                    }
                                  >
                                    {[
                                      { description: 'Yes', value: 'Yes' },
                                      { description: 'No', value: 'No' }
                                    ]?.map((set: IDropdownResponse, index) => {
                                      return (
                                        <MenuItem key={`${index + 1}`} value={set.value}>
                                          {set.description}
                                        </MenuItem>
                                      );
                                    })}
                                  </SMBSelect>
                                </Grid>
                                {values?.alwaysDisplayInOptionsReport === 'No' && (
                                  <>
                                    <Grid item xs={12} sm={6}>
                                      <SMBSelect
                                        disabled={isLoadingStandardFeatureBuildOptions}
                                        required
                                        onChange={e => {
                                          setCategory(e.target.value);
                                          setSelectedSubcategoryValues([]);
                                          setFieldValue('upgradeOptions', []);
                                        }}
                                        variant="outlined"
                                        name={`category`}
                                        label={
                                          isLoadingStandardFeatureBuildOptions
                                            ? 'Loading...'
                                            : 'Category'
                                        }
                                        id={`category-select`}
                                        value={selectedCategory}
                                      >
                                        {categoryOptions?.map((category, index) => (
                                          <MenuItem key={index} value={category}>
                                            {category}
                                          </MenuItem>
                                        ))}
                                      </SMBSelect>
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Autocomplete
                                        multiple
                                        disabled={!selectedCategory}
                                        id="subcategories"
                                        options={selectedCategorySubcategories}
                                        getOptionLabel={(option: any) => option?.subcategory}
                                        onChange={(event, newValue) => {
                                          setSelectedSubcategoryValues(newValue);

                                          const validOptions = newValue.flatMap(subcategory =>
                                            subcategory.options.map(option => ({
                                              optionName: option.optionName,
                                              optionSetId: option.optionSetId
                                            }))
                                          );

                                          const filteredSelectedOptions =
                                            values.upgradeOptions.filter(selectedOption =>
                                              validOptions.some(
                                                validOption =>
                                                  validOption.optionName ===
                                                    selectedOption.optionName &&
                                                  validOption.optionSetId ===
                                                    selectedOption.optionSetId
                                              )
                                            );

                                          setFieldValue('upgradeOptions', filteredSelectedOptions);
                                        }}
                                        value={selectedSubcategoryValues}
                                        renderInput={params => (
                                          <TextField
                                            {...params}
                                            label="Subcategories *"
                                            placeholder="Select Subcategories"
                                          />
                                        )}
                                      />
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Autocomplete
                                        disabled={!selectedSubcategoryValues.length}
                                        multiple
                                        id="upgradeOptions"
                                        options={selectedOptions}
                                        getOptionLabel={(option: IUpgradeOptions) =>
                                          option.optionName
                                        }
                                        value={values.upgradeOptions}
                                        onChange={(event, newValue) => {
                                          setFieldValue('upgradeOptions', newValue);
                                        }}
                                        isOptionEqualToValue={(option, value) =>
                                          option.optionName === value.optionName &&
                                          option.optionSetId === value.optionSetId
                                        }
                                        renderInput={params => (
                                          <TextField
                                            {...params}
                                            label="Options *"
                                            placeholder="Select Options"
                                          />
                                        )}
                                      />
                                    </Grid>
                                  </>
                                )}
                              </Grid>

                              <Grid item xs={12}>
                                <Grid container spacing={1} alignItems="center">
                                  <Grid item xs={12} sm={6}>
                                    <StandardFeatureImages
                                      standardFeatureId={standardFeatureId}
                                      imageTitle={'Image'}
                                      image={values.imageUrl}
                                      imageData={value => setImageFormData(value)}
                                      refetchImage={refetchstandardFeatureDetail}
                                    ></StandardFeatureImages>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </div>
                            <Box mt={1} mb={0.5}>
                              <Divider />
                            </Box>
                            <ButtonGroup>
                              <Button
                                disabled={
                                  !dirty ||
                                  isSubmitting ||
                                  !isValid ||
                                  (values?.includeInCalculator === 'Yes' &&
                                    !values?.normalRetailRate) ||
                                  (values?.alwaysDisplayInOptionsReport === 'No' &&
                                    values?.upgradeOptions?.length === 0)
                                }
                                type="submit"
                                startIcon={isEdit ? <Save /> : <Add />}
                                variant="contained"
                                color="primary"
                              >
                                {isEdit ? 'Save' : 'Add'}
                              </Button>
                              <Button
                                type="button"
                                variant="contained"
                                color="inherit"
                                startIcon={<Close />}
                                onClick={() => {
                                  if (!deepEqual(initialValues, values)) {
                                    const result = window.confirm(
                                      'You have unsaved changes, are you sure you want to exit?'
                                    );
                                    if (result) {
                                      resetForm();
                                      handleClose();
                                    } else {
                                      return;
                                    }
                                  } else {
                                    handleClose();
                                  }
                                }}
                              >
                                Cancel
                              </Button>
                            </ButtonGroup>
                          </Paper>
                        </CardContent>
                      </CardMui>
                    </Grid>
                  </Grid>
                </Form>
              </Fade>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  primaryHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    marginBottom: theme.spacing(1)
  },
  marginBottom: {
    marginBottom: theme.spacing(1)
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    '& > div:not(:first-of-type)': {
      marginTop: theme.spacing(1)
    }
  },
  outlinedLabel: {
    backgroundColor: theme.palette.common.white,
    paddingLeft: 2,
    paddingRight: 2
  },
  content: {
    marginTop: theme.spacing(1)
  },
  cardContent: {
    paddingTop: 0
  },
  paperBorder: {
    border: `1px solid ${theme.palette.grey[300]}`
  }
}));
